import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Send, Plus, X } from 'lucide-react';
import { AnecdoteAIAssistant } from './AnecdoteAIAssistant';

// Modal used by the co-promoter to propose new content to a specific field
// or list of the promoter's form. Small, focused component kept out of
// ClientFormPanel to avoid growing that file further.

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CoPromoterSuggestionDialog = (props) => {
  const {
    open,
    onOpenChange,
    token,
    mode,
    sectionId,
    fieldId,
    fieldLabel,
    listItemFields,
    currentValue,
    sectionTitle,
    onSubmitted,
  } = props;
  const listFields = listItemFields || [];
  const [value, setValue] = useState('');
  const [itemValues, setItemValues] = useState({});
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [aiPhotos, setAiPhotos] = useState([]);

  useEffect(() => {
    if (open) {
      setValue('');
      setItemValues({});
      setNote('');
      setAiPhotos([]);
    }
  }, [open]);

  const isList = mode === 'add_list_item';
  // AI assistance targets the first multiline field (the anecdote/free text).
  const aiTargetFieldId = isList ? (listFields.find(f => f.multiline)?.id || null) : '__value__';
  const aiText = isList ? (aiTargetFieldId ? (itemValues[aiTargetFieldId] || '') : '') : value;
  const applyAiText = (t) => {
    if (isList) {
      if (aiTargetFieldId) setItemValues(prev => ({ ...prev, [aiTargetFieldId]: t }));
    } else {
      setValue(t);
    }
  };
  const aiContext = isList
    ? listFields
        .filter(f => f.id !== aiTargetFieldId && String(itemValues[f.id] || '').trim())
        .map(f => `${f.label}: ${itemValues[f.id]}`)
        .join('\n')
    : '';
  const canSubmit = !sending && (
    isList
      ? Object.values(itemValues).some(v => (v ?? '').toString().trim().length > 0)
      : value.trim().length > 0
  );

  const submit = async () => {
    setSending(true);
    try {
      const listValue = aiPhotos.length > 0 ? { ...itemValues, photos: aiPhotos } : itemValues;
      const payload = isList
        ? { section_id: sectionId, operation: 'add_list_item', value: listValue, note }
        : { section_id: sectionId, field_id: fieldId, operation: 'add_text', value: value.trim(), note };
      if (isList && fieldId) payload.field_id = fieldId; // inline list inside a section
      const res = await axios.post(`${API_URL}/api/co-promote/${token}/suggestions`, payload);
      toast.success('Aportación registrada. El promotor la revisará.');
      onSubmitted?.(res.data.suggestion);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar la aportación');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="co-promoter-suggestion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            {isList ? `Añadir un nuevo elemento a «${fieldLabel}»` : `Añadir contenido a «${fieldLabel}»`}
          </DialogTitle>
          <DialogDescription>
            Tu aportación se enviará al promotor del libro. Él decidirá si la incorpora al contenido final.
          </DialogDescription>
        </DialogHeader>

        {!isList && currentValue && (
          <div className="text-xs bg-muted/40 border border-muted rounded-md p-3 space-y-1">
            <p className="font-medium text-muted-foreground">Contenido actual del promotor</p>
            <p className="whitespace-pre-wrap text-foreground/80 line-clamp-5">{currentValue}</p>
          </div>
        )}

        <div className="space-y-4">
          {isList ? (
            listFields.map(f => (
              <div key={f.id} className="space-y-1.5">
                <Label htmlFor={`cps-${f.id}`} className="text-sm">
                  {f.label}
                </Label>
                {f.options ? (
                  <select
                    id={`cps-${f.id}`}
                    value={itemValues[f.id] || ''}
                    onChange={e => setItemValues(prev => ({ ...prev, [f.id]: e.target.value }))}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid={`cps-field-${f.id}`}
                  >
                    <option value="">Selecciona…</option>
                    {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : f.multiline ? (
                  <Textarea
                    id={`cps-${f.id}`}
                    value={itemValues[f.id] || ''}
                    onChange={e => setItemValues(prev => ({ ...prev, [f.id]: e.target.value }))}
                    placeholder={f.placeholder}
                    rows={3}
                    data-testid={`cps-field-${f.id}`}
                  />
                ) : (
                  <Input
                    id={`cps-${f.id}`}
                    value={itemValues[f.id] || ''}
                    onChange={e => setItemValues(prev => ({ ...prev, [f.id]: e.target.value }))}
                    placeholder={f.placeholder}
                    data-testid={`cps-field-${f.id}`}
                  />
                )}
              </div>
            ))
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="cps-value" className="text-sm">Tu aportación</Label>
              <Textarea
                id="cps-value"
                value={value}
                onChange={e => setValue(e.target.value)}
                rows={5}
                placeholder="Escribe aquí el contenido que quieres aportar…"
                data-testid="cps-value"
              />
            </div>
          )}

          {aiTargetFieldId && (
            <AnecdoteAIAssistant
              apiBase={`${API_URL}/api/co-promote/${token}`}
              withCreds={false}
              text={aiText}
              stage={sectionTitle || fieldLabel || ''}
              context={aiContext}
              enablePhotos={isList}
              onApplyText={applyAiText}
              onAddPhotos={(chosen) => {
                const room = Math.max(0, 2 - aiPhotos.length);
                const mapped = chosen.slice(0, room).map(ph => ({
                  photo: { photo_id: ph.photo_id, url: `${API_URL}${ph.url}`, storage_path: ph.storage_path },
                  caption: ph.caption || '',
                }));
                if (mapped.length > 0) {
                  setAiPhotos(prev => [...prev, ...mapped]);
                  toast.success('Fotos añadidas a tu aportación');
                }
              }}
              testIdPrefix="cps-ai"
            />
          )}

          {aiPhotos.length > 0 && (
            <div className="flex gap-2 flex-wrap" data-testid="cps-ai-photos-preview">
              {aiPhotos.map((p, i) => (
                <div key={i} className="relative w-16 h-16 rounded-md overflow-hidden border">
                  <img src={p.photo.url} alt={p.caption || 'Foto IA'} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setAiPhotos(prev => prev.filter((_, j) => j !== i))}
                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center"
                    data-testid={`cps-ai-photo-remove-${i}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="cps-note" className="text-sm text-muted-foreground">Nota para el promotor (opcional)</Label>
            <Textarea
              id="cps-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              placeholder="Ej.: lo contó la abuela Carmen hace años…"
              data-testid="cps-note"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancelar</Button>
          <Button onClick={submit} disabled={!canSubmit} className="gap-2" data-testid="cps-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviar aportación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CoPromoterSuggestionDialog;
